import { CheckCircleIcon, InfoIcon, WarningCircleIcon, XCircleIcon } from '@phosphor-icons/react'
import { useState } from 'react'

import { IglooPin } from '@/api/keyless/igloo/normalize-igloo-pin'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { formatDateOnly } from '@/utils/date'

import * as IS from './igloo-tab.styled'

// Copy is specified verbatim in the tickets. Note: EL-11402 says "a permanent Igloo PIN" —
// dropped, because the live API issues a DAILY algoPIN bound to the lease end (29-367 days).
// Calling it permanent in staff-facing copy would be wrong.
const PIN_TOOLTIP =
  'Issue an Igloo PIN as a backup access method for this resident. Residents should primarily ' +
  'use BLE keyless access in the ElevateOS app. PIN access could be issued upon request. The PIN ' +
  'will be sent to the resident by email. Revoking the PIN in ElevateOS removes the PIN record ' +
  'from ElevateOS, but disabling it on the physical lock may require Bluetooth sync and lock ' +
  'reset based on Igloo behavior.'

// EL-11408, verbatim.
const SYNC_TOOLTIP =
  'Upon completion of the revocation process, staff are required to physically approach the ' +
  'resident\'s lock and utilize the "Revoke PIN" feature within the Elevated Hub mobile ' +
  'application to ensure the PIN code is fully removed from the device.'

// hasIglooAccessId === false -> ElevateOS has no Igloo access id for this PIN, so DELETE /pin
// cannot remove it on the Igloo side. Staff must escalate.
const NO_ACCESS_ID_WARNING =
  'Cannot revoke this PIN from ElevateOS — it is not linked to Iglooworks. Contact support to ' +
  'remove it.'

interface IglooPinBlockProps {
  pin: IglooPin
  canEdit: boolean
  residentName: string
  onIssue: () => Promise<void>
  onRevoke: () => Promise<void>
  onResend: () => Promise<void>
  isIssuing: boolean
  isRevoking: boolean
  isResending: boolean
}

export const IglooPinBlock = ({
  pin,
  canEdit,
  residentName,
  onIssue,
  onRevoke,
  onResend,
  isIssuing,
  isRevoking,
  isResending,
}: IglooPinBlockProps) => {
  const [showIssueConfirm, setShowIssueConfirm] = useState(false)
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)

  if (pin.status === 'none') {
    if (!canEdit) return null
    return (
      <IS.PinBlock>
        <IS.IssueRow>
          <Button
            use="primary"
            size="small"
            loading={isIssuing}
            onClick={() => setShowIssueConfirm(true)}
          >
            Issue Unit PIN Code Access
          </Button>
          <Tooltip triggerIcon={InfoIcon}>{PIN_TOOLTIP}</Tooltip>
        </IS.IssueRow>
        {showIssueConfirm && (
          <ConfirmationPopup
            title="Issue Unit PIN Code Access"
            variant="warning"
            confirmText="Yes, issue"
            onClose={() => setShowIssueConfirm(false)}
            onConfirm={async () => {
              await onIssue()
              setShowIssueConfirm(false)
            }}
          >
            This will issue a Unit PIN code for this resident and email it to them. Do you wish to
            proceed?
          </ConfirmationPopup>
        )}
      </IS.PinBlock>
    )
  }

  if (pin.status === 'revoked_pending_sync' || pin.status === 'revoked') {
    // Only show a date sourced from an actual revocation timestamp — never fall back to
    // issuedAt (an explicit backend status:'revoked' can arrive without revokedAt).
    const revokedOn = pin.revokedAt
    return (
      <IS.PinBlock>
        <IS.IssuedInfo>
          <Typography size="text-medium" color="black800" as="span">
            PIN Revoked{revokedOn ? ` ${formatDateOnly(revokedOn)}` : ''}
          </Typography>
          <Icon icon={CheckCircleIcon} size="1.8rem" color="green600" />
          {pin.status === 'revoked_pending_sync' && (
            <>
              <Typography size="text-medium" color="black500" as="span">
                |
              </Typography>
              <Typography size="text-medium" color="black800" as="span">
                Unit lock sync: Required
              </Typography>
              <Tooltip triggerIcon={InfoIcon}>{SYNC_TOOLTIP}</Tooltip>
            </>
          )}
        </IS.IssuedInfo>
      </IS.PinBlock>
    )
  }

  // status === 'active'
  return (
    <IS.PinBlock>
      <Flex align="center" justify="space-between" gap={12} wrap="wrap">
        <Typography size="text-medium" color="black800" as="span">
          Unit PIN Code Issued{pin.issuedAt ? ` on ${formatDateOnly(pin.issuedAt)}` : ''}
        </Typography>
        <Flex align="center" gap={8} wrap="wrap">
          {/* EL-11407 attaches Resend to the "mail not sent" state, which covers both an
              outright failure and a send that never landed (pending). */}
          {canEdit && (pin.emailStatus === 'failed' || pin.emailStatus === 'pending') && (
            <Button
              use="primary"
              size="small"
              loading={isResending}
              onClick={() => void onResend()}
            >
              Resend an email
            </Button>
          )}
          {canEdit && (
            <Button
              use="primary"
              size="small"
              loading={isRevoking}
              onClick={() => setShowRevokeConfirm(true)}
            >
              Revoke Unit PIN Code Access
            </Button>
          )}
          <Tooltip triggerIcon={InfoIcon}>{PIN_TOOLTIP}</Tooltip>
        </Flex>
      </Flex>
      <Flex align="center" gap={8}>
        <Typography size="text-medium" color="primary500" as="span">
          Email status:
        </Typography>
        {pin.emailStatus === 'failed' ? (
          <>
            <Icon icon={XCircleIcon} size="1.8rem" color="red600" />
            <Typography size="text-medium" color="red600" weight="medium" as="span">
              Failed
            </Typography>
          </>
        ) : pin.emailStatus === 'sent' ? (
          <>
            <Icon icon={CheckCircleIcon} size="1.8rem" color="green600" />
            <Typography size="text-medium" color="green600" weight="medium" as="span">
              Sent
            </Typography>
          </>
        ) : (
          <Typography size="text-medium" color="black500" weight="medium" as="span">
            Pending
          </Typography>
        )}
      </Flex>
      {!pin.hasIglooAccessId && (
        <IS.PinWarning>
          <Icon icon={WarningCircleIcon} size="1.8rem" color="red600" />
          <Typography size="text-medium" color="red600" as="span">
            {NO_ACCESS_ID_WARNING}
          </Typography>
        </IS.PinWarning>
      )}
      {showRevokeConfirm && (
        <ConfirmationPopup
          title="Revoke PIN access?"
          variant="danger"
          confirmText="Revoke PIN"
          cancelText="Cancel"
          onClose={() => setShowRevokeConfirm(false)}
          onConfirm={async () => {
            await onRevoke()
            setShowRevokeConfirm(false)
          }}
        >
          Are you sure you want to revoke PIN access for {residentName || 'this resident'}? This
          will remove the PIN record from ElevateOS and the resident will no longer receive this PIN
          through ElevateOS. But the PIN remains active on the physical lock until the lock is
          reset.
        </ConfirmationPopup>
      )}
    </IS.PinBlock>
  )
}
